import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Ensure autoTable is attached (sometimes needed for typescript depending on import logic)
// const jsPDF = require('jspdf');
// require('jspdf-autotable');

interface ReportHeaderOptions {
    title: string;
    subtitle?: string;
    period?: { start: string | Date; end: string | Date };
}

export class PDFGenerator {
    private doc: jsPDF;

    constructor(orientation: 'p' | 'l' = 'p') {
        this.doc = new jsPDF({
            orientation: orientation,
            unit: 'mm',
            format: 'letter' // Carta
        });
    }

    private addHeader(options: ReportHeaderOptions) {
        const doc = this.doc;
        const pageWidth = doc.internal.pageSize.width;

        // Logo / Brand Name
        doc.setFontSize(18);
        doc.text('MARIA REGNA', 14, 20);

        doc.setFontSize(10);
        doc.text('ERP System', 14, 25);

        // Report Title
        doc.setFontSize(16);
        doc.text(options.title, pageWidth - 14, 20, { align: 'right' });

        // Subtitle / Period
        if (options.subtitle) {
            doc.setFontSize(10);
            doc.text(options.subtitle, pageWidth - 14, 26, { align: 'right' });
        }

        if (options.period) {
            const start = typeof options.period.start === 'string' ? options.period.start : format(new Date(options.period.start), 'dd/MM/yyyy');
            const end = typeof options.period.end === 'string' ? options.period.end : format(new Date(options.period.end), 'dd/MM/yyyy');
            doc.setFontSize(9);
            doc.setTextColor(100);
            doc.text(`Periodo: ${start} - ${end}`, pageWidth - 14, 31, { align: 'right' });
            doc.setTextColor(0);
        }

        // Divider Line
        doc.setLineWidth(0.5);
        doc.line(14, 35, pageWidth - 14, 35);
    }

    private addFooter() {
        const doc = this.doc;
        const pageCount = doc.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150);
            const text = `Página ${i} de ${pageCount} - Generado el ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: es })}`;
            doc.text(text, pageWidth / 2, pageHeight - 10, { align: 'center' });
        }
    }

    public generateTable(columns: string[], body: any[], title: string, period?: any) {
        this.addHeader({ title, period });

        autoTable(this.doc, {
            startY: 40,
            head: [columns],
            body: body,
            theme: 'grid',
            headStyles: { fillColor: [66, 66, 66], textColor: 255 },
            styles: { fontSize: 8 },
            margin: { top: 40, bottom: 20 },
            didDrawPage: () => {
                // Header is drawn initially, for subsequent pages autotable repeats headers.
                // If we wanted custom headers on each page we could hook here.
            }
        });

        this.addFooter();
    }

    public getBlobUrl(): string {
        return this.doc.output('bloburl') as unknown as string;
    }

    public save(filename: string) {
        this.doc.save(filename);
    }

    // --- Specific Report Generators ---

    public static createSalesReport(data: any[], period: { start: Date, end: Date }) {
        const generator = new PDFGenerator('l'); // Landscape for detailed rows

        const columns = ['Folio', 'Fecha', 'Cliente', 'Items', 'Total', 'Pagado', 'Pendiente', 'Estado'];
        const body = data.map(item => [
            item.folio,
            format(new Date(item.fecha), 'dd/MM/yyyy HH:mm'),
            item.cliente,
            item.items || `${item.totalItems || 0} items`,
            `$${Number(item.total).toFixed(2)}`,
            item.pagado !== undefined ? `$${Number(item.pagado).toFixed(2)}` : '-',
            item.pendiente !== undefined ? `$${Number(item.pendiente).toFixed(2)}` : '-',
            item.estado
        ]);

        generator.generateTable(columns, body, 'Reporte de Ventas', period);
        return generator;
    }

    public static createPreOrderReport(data: any[]) {
        const generator = new PDFGenerator('l');
        const columns = ['Folio', 'Fecha', 'Cliente', 'Teléfono', 'Items', 'Total', 'Pagado', 'Restante', 'Estado'];
        const body = data.map(item => [
            item.folio,
            format(new Date(item.fecha), 'dd/MM/yyyy'),
            item.cliente,
            item.telefono || 'N/A',
            item.items,
            `$${Number(item.total).toFixed(2)}`,
            `$${Number(item.pagado).toFixed(2)}`,
            `$${Number(item.restante).toFixed(2)}`,
            item.estado
        ]);

        generator.generateTable(columns, body, 'Reporte de PRE-ORDERs');
        return generator;
    }

    public static createInventoryReport(data: any[], title: string = 'Reporte de Inventario') {
        const generator = new PDFGenerator('p');
        const columns = ['Código', 'Marca', 'Color', 'Talla', 'Stock', 'Precio', 'Estado'];
        const body = data.map(item => [
            item.cod_producto,
            item.marca,
            item.color,
            item.talla,
            item.stock,
            `$${item.precio}`,
            item.ativo ? 'Activo' : 'Inactivo'
        ]);

        generator.generateTable(columns, body, title);
        return generator;
    }

    public static createInvoiceReport(data: any[], period?: any) {
        const generator = new PDFGenerator('p');
        const columns = ['Invoice ID', 'Fecha Creación', 'Fecha Recepción', 'Items', 'Estado'];
        const body = data.map(item => [
            item.id,
            item.fecha ? format(new Date(item.fecha), 'dd/MM/yyyy') : '-',
            item.fechaRecepcion ? format(new Date(item.fechaRecepcion), 'dd/MM/yyyy') : '-',
            item.totalItems,
            item.estado
        ]);

        generator.generateTable(columns, body, 'Reporte de Invoices (Entradas)', period);
        return generator;
    }
}
